import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';

import { useAbility } from '@features/authorization';
import {
  isCrmCustomerDocumentFullPagePath,
  projectCrmCustomerDocumentsListSearch,
  type CrmCustomerDocumentsListSearch,
} from '@features/crm-customer-documents';
import { CrmCustomerDocumentsPage } from '@pages/crm';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessCrmCustomerDocuments } from '@shared/lib';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/documents'
)({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessCrmCustomerDocuments(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: CrmCustomerDocumentsRoute,
});

function CrmCustomerDocumentsRoute() {
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const ability = useAbility();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (!canAccessCrmCustomerDocuments(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  if (isCrmCustomerDocumentFullPagePath(pathname, customerId)) return <Outlet />;

  return (
    <CrmCustomerDocumentsPage
      documentType={search.documentType}
      fileState={search.documentFileState}
      page={search.documentPage ?? 1}
      q={search.documentQ}
      registrationId={search.documentRegistrationId}
      sort={search.documentSort ?? 'updatedAt.desc'}
      status={search.documentStatus}
      onCreateDocument={() => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/documents/new',
          params: { userProfileId: customerId, locale },
          search: (current) => ({
            ...current,
            documentRegistrationId: search.documentRegistrationId,
          }),
        });
      }}
      onEditDocument={(documentId) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/documents/$documentId',
          params: { userProfileId: customerId, documentId, locale },
          search: (current: Record<string, unknown>) => ({
            ...current,
            ...projectCrmCustomerDocumentsListSearch(
              current as unknown as Partial<CrmCustomerDocumentsListSearch>
            ),
            documentTab: undefined,
            edit: true,
            learningRegistrationId: search.documentRegistrationId,
          }),
        });
      }}
      onOpenDocument={(documentId, tab) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/documents/$documentId',
          params: { userProfileId: customerId, documentId, locale },
          search: (current: Record<string, unknown>) => ({
            ...current,
            ...projectCrmCustomerDocumentsListSearch(
              current as unknown as Partial<CrmCustomerDocumentsListSearch>
            ),
            documentTab: tab === 'general' ? undefined : tab,
            edit: undefined,
            learningRegistrationId: search.documentRegistrationId,
          }),
        });
      }}
      onStateChange={(next, options) => {
        void navigate({
          replace: options?.replace ?? false,
          search: (current) => ({
            ...current,
            ...projectCrmCustomerDocumentsListSearch(current),
            documentFileState: next.fileState,
            documentPage: next.page === 1 ? undefined : next.page,
            documentQ: next.q,
            documentRegistrationId: next.registrationId,
            documentSort: next.sort === 'updatedAt.desc' ? undefined : next.sort,
            documentStatus: next.status,
            documentType: next.documentType,
          }),
        });
      }}
    />
  );
}
